import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Client } from '@/types/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  Building2, 
  Calendar,
  MoreVertical,
  User,
  MessageCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface ClientKanbanCardProps {
  client: Client;
  isDragging?: boolean;
  viewMode?: 'comfortable' | 'compact' | 'spacious';
}

const statusColors: Record<Client['status'], string> = {
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  waiting: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  waiting_supplier: 'bg-orange-100 text-orange-800 border-orange-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  pricing_requested: 'bg-purple-100 text-purple-800 border-purple-200',
  pricing_sent: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  booking_confirmed: 'bg-teal-100 text-teal-800 border-teal-200',
  traveling: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  customer_service: 'bg-pink-100 text-pink-800 border-pink-200',
};

const statusLabels: Record<Client['status'], string> = {
  confirmed: 'مؤكد',
  waiting: 'في الانتظار',
  in_progress: 'قيد التنفيذ',
  waiting_supplier: 'انتظار المورد',
  cancelled: 'ملغي',
  pricing_requested: 'طلب تسعير',
  pricing_sent: 'تم إرسال التسعير',
  booking_confirmed: 'تأكيد الحجز',
  traveling: 'في السفر',
  customer_service: 'خدمة العملاء',
};

export const ClientKanbanCard: React.FC<ClientKanbanCardProps> = ({ 
  client, 
  isDragging = false,
  viewMode = 'comfortable'
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: client.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const timeAgo = formatDistanceToNow(new Date(client.created_at), {
    addSuffix: true,
    locale: ar,
  });

  if (isDragging || isSortableDragging) {
    return (
      <Card 
        ref={setNodeRef} 
        style={style}
        className={cn(
          "cursor-grabbing opacity-50 rotate-3 scale-105",
          "border-primary shadow-xl bg-card"
        )}
        {...attributes}
        {...listeners}
      >
        <CardContent className={cn(
          viewMode === 'compact' ? 'p-3' : 
          viewMode === 'spacious' ? 'p-5' : 'p-4'
        )}>
          <div className={cn(
            viewMode === 'compact' ? 'space-y-2' : 
            viewMode === 'spacious' ? 'space-y-4' : 'space-y-3'
          )}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Avatar className={cn(
                  viewMode === 'compact' ? 'h-8 w-8' : 
                  viewMode === 'spacious' ? 'h-12 w-12' : 'h-10 w-10'
                )}>
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(client.name_ar)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h4 className={cn(
                    "font-medium truncate",
                    viewMode === 'compact' ? 'text-xs' : 
                    viewMode === 'spacious' ? 'text-base' : 'text-sm'
                  )}>{client.name_ar}</h4>
                  {client.name_en && (
                    <p className={cn(
                      "text-muted-foreground truncate",
                      viewMode === 'compact' ? 'text-xs' : 
                      viewMode === 'spacious' ? 'text-sm' : 'text-xs'
                    )}>{client.name_en}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      ref={setNodeRef} 
      style={style}
      className={cn(
        "cursor-grab hover:shadow-md transition-all duration-200",
        "border bg-card hover:scale-[1.02] active:scale-95",
        "hover:border-primary/20"
      )}
      {...attributes}
      {...listeners}
    >
      <CardContent className={cn(
        viewMode === 'compact' ? 'p-3' : 
        viewMode === 'spacious' ? 'p-5' : 'p-4'
      )}>
        <div className={cn(
          viewMode === 'compact' ? 'space-y-2' : 
          viewMode === 'spacious' ? 'space-y-4' : 'space-y-3'
        )}>
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar className={cn(
                "shrink-0",
                viewMode === 'compact' ? 'h-8 w-8' : 
                viewMode === 'spacious' ? 'h-12 w-12' : 'h-10 w-10'
              )}>
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {getInitials(client.name_ar)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h4 className={cn(
                  "font-medium truncate",
                  viewMode === 'compact' ? 'text-xs' : 
                  viewMode === 'spacious' ? 'text-base' : 'text-sm'
                )}>{client.name_ar}</h4>
                {client.name_en && (
                  <p className={cn(
                    "text-muted-foreground truncate",
                    viewMode === 'compact' ? 'text-xs' : 
                    viewMode === 'spacious' ? 'text-sm' : 'text-xs'
                  )}>{client.name_en}</p>
                )}
              </div>
            </div>
            
            {viewMode !== 'compact' && (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0">
                <MoreVertical className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Company */}
          {client.company && viewMode !== 'compact' && (
            <div className={cn(
              "flex items-center gap-2 text-muted-foreground",
              viewMode === 'spacious' ? 'text-sm' : 'text-xs'
            )}>
              <Building2 className={cn(
                "shrink-0",
                viewMode === 'spacious' ? 'h-4 w-4' : 'h-3 w-3'
              )} />
              <span className="truncate">{client.company}</span>
            </div>
          )}

          {/* Contact */}
          {client.mobile && (
            <div className={cn(
              "flex items-center gap-2",
              viewMode === 'compact' ? 'text-xs' : 
              viewMode === 'spacious' ? 'text-sm' : 'text-xs'
            )}>
              <Phone className={cn(
                "shrink-0 text-muted-foreground",
                viewMode === 'spacious' ? 'h-4 w-4' : 'h-3 w-3'
              )} />
              <span className="font-mono dir-ltr text-left">{client.mobile}</span>
            </div>
          )}

          {/* Additional Info */}
          {client.additional_info && viewMode === 'spacious' && (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <MessageCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p className="line-clamp-2 text-right">{client.additional_info}</p>
            </div>
          )}

          {/* Footer */}
          <div className={cn(
            "flex items-center justify-between",
            viewMode === 'compact' ? 'text-xs' : 
            viewMode === 'spacious' ? 'text-sm' : 'text-xs'
          )}>
            <Badge variant="outline" className={cn(
              statusColors[client.status],
              viewMode === 'compact' ? 'text-xs px-2 py-0' : 
              viewMode === 'spacious' ? 'text-sm px-3 py-1' : 'text-xs'
            )}>
              {statusLabels[client.status]}
            </Badge>
            
            {viewMode !== 'compact' && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Calendar className={cn(
                  viewMode === 'spacious' ? 'h-4 w-4' : 'h-3 w-3'
                )} />
                <span>{timeAgo}</span>
              </div>
            )}
          </div>

          {/* Creator */}
          {client.creator && 'name' in client.creator && viewMode === 'spacious' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground border-t pt-3">
              <User className="h-4 w-4 shrink-0" />
              <span className="truncate">تم الإنشاء بواسطة: {client.creator.name}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};